import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';

import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { filter, map, switchMap, withLatestFrom } from 'rxjs/operators';

import { wsLink } from '../../features/shared/utils/workspace-link';
import { ChecklistListActions } from './checklist-list.actions';
import { ChecklistService } from '../../features/checklist/services/checklist.service';
import { SUCCESS_NOTIFICATION_CONFIGURATION } from '../../features/shared/constants/notification-configurations';
import { SemerisRoutes } from '../../features/shared/constants/routes';
import { WebSocketEntities } from '../../features/shared/models/web-socket/web-socket.models';
import { createWebSocketEffect } from '../../features/shared/services/web-socket/create-web-socket-effect';
import { WebSocketService } from '../../features/shared/services/web-socket/web-socket.service';
import {
  createNotificationEffect,
  PushNotificationRecord,
} from '../../features/shared/utils/create-notification-effect';
import { reportEffectError } from '../../features/shared/utils/report-effect-error';
import { AssetSettingInput } from '../../generated/graphql-models';
import { AppActions } from '../app/app.actions';
import { ChecklistActions } from '../checklist/checklist.actions';
import { ExtractActions } from '../extract/extract.actions';
import { RootState } from '../root-state.model';
import { UserProfileActions } from '../user-profile/user-profile.actions';

@Injectable()
export class ChecklistListEffects {
  private service = inject(ChecklistService);
  private actions$ = inject(Actions);
  private readonly store = inject<Store<RootState>>(Store);
  private readonly webSocketService = inject(WebSocketService);
  private readonly router = inject(Router);

  private readonly notifications: PushNotificationRecord = {
    [ChecklistListActions.CreateChecklistSuccess.type]: {
      message: 'Checklist created',
      isSuccess: true,
    },
    [ChecklistListActions.CreateChecklistFail.type]: {
      message: 'Failed to create the checklist',
      isSuccess: false,
    },
    [ChecklistListActions.DeleteSuccess.type]: { message: 'Checklist deleted', isSuccess: true },
    [ChecklistListActions.DeleteFail.type]: {
      message: 'Failed to delete the checklist',
      isSuccess: false,
    },
    [ExtractActions.UpdateExtractOrderSuccess.type]: {
      message: 'My topics updated',
      isSuccess: true,
    },
    [ExtractActions.UpdateExtractOrderFail.type]: {
      message: 'Failed to update My topics',
      isSuccess: false,
    },
    [ChecklistListActions.CloneModelSuccess.type]: { message: 'Checklist cloned', isSuccess: true },
    [ChecklistListActions.CloneModelFail.type]: {
      message: 'Failed to clone the checklist',
      isSuccess: false,
    },
  };

  notificationEffect$: Observable<Action> = createNotificationEffect(
    this.actions$,
    this.notifications
  );

  loadListEffect$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(ChecklistListActions.LoadList),
      withLatestFrom(this.store),
      switchMap(([action, store]) =>
        this.service.getAll().pipe(
          map((list) => ChecklistListActions.LoadListSuccess({ list })),
          reportEffectError(ChecklistListActions.LoadListFail)
        )
      )
    )
  );

  deleteEffect$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(ChecklistListActions.Delete),
      withLatestFrom(this.store),
      switchMap(([action, store]) =>
        this.service.delete(action.checklist.id).pipe(
          map(() => ChecklistListActions.DeleteSuccess({ checklist: action.checklist })),
          reportEffectError(ChecklistListActions.DeleteFail)
        )
      )
    )
  );

  createChecklistEffect$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(ChecklistListActions.CreateChecklist),
      withLatestFrom(this.store),
      switchMap(([action, store]) =>
        this.service.create(action.checklist).pipe(
          map((checklist) => ChecklistListActions.CreateChecklistSuccess({ checklist })),
          reportEffectError(ChecklistListActions.CreateChecklistFail)
        )
      )
    )
  );

  templateModalClosedEffect$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.TemplateModalClosed),
      filter((action) => action.modalId === 'edit-checklist' && !!action.data),
      filter((action) => !action.data.id),
      map((action) =>
        ChecklistListActions.CreateChecklist({
          checklist: { ...action.data, id: undefined, topics: [] } as any,
        })
      )
    )
  );

  createChecklistSuccessNavigateEffect$: Observable<any> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ChecklistListActions.CreateChecklistSuccess),
        map((action) =>
          this.router.navigate(wsLink(SemerisRoutes.checklist.root, action.checklist.id))
        )
      ),
    { dispatch: false }
  );

  selectChecklistSaveEffect$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(ChecklistListActions.SelectChecklist),
      filter((action) => !action.skipSave),
      map((action) =>
        UserProfileActions.UpdateAssetSetting({
          setting: {
            selectedChecklistId: action.id,
          },
        })
      )
    )
  );

  selectAutoPublishedChecklistSaveEffect$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(ChecklistListActions.SetAutoPublishedChecklists),
      map((action) =>
        UserProfileActions.UpdateMultipleAssetSetting({
          settings: Object.keys(action.checklistByAsset || {}).reduce<AssetSettingInput[]>(
            (acc, assetShortName) => {
              acc.push({
                assetShortName,
                autoPublishedChecklistId: action.checklistByAsset[assetShortName],
              });
              return acc;
            },
            []
          ),
          id: 'update-auto-published-checklists',
        })
      )
    )
  );

  selectAutoPublishedChecklistSaveNotificationEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserProfileActions.UpdateMultipleAssetSettingSuccess),
      filter((action) => action.id === 'update-auto-published-checklists'),
      map((action) =>
        AppActions.PushNotification({
          message: 'Default checklists updated',
          config: SUCCESS_NOTIFICATION_CONFIGURATION,
        })
      )
    )
  );

  selectAutoPublishedChecklistRefreshEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserProfileActions.UpdateMultipleAssetSettingSuccess),
      filter((action) => action.id === 'update-auto-published-checklists'),
      map((action) => ChecklistListActions.LoadList())
    )
  );

  cloneModelEffect$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(ChecklistListActions.CloneModel),
      withLatestFrom(this.store),
      switchMap(([action, store]) =>
        this.service.clone(action.id).pipe(
          map((checklist) => ChecklistListActions.CloneModelSuccess({ checklist })),
          reportEffectError(ChecklistListActions.CloneModelFail)
        )
      )
    )
  );

  cloneChecklistSuccessNavigateEffect$: Observable<any> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ChecklistListActions.CloneModelSuccess),
        map((action) =>
          this.router.navigate(wsLink(SemerisRoutes.checklist.root, action.checklist.id))
        )
      ),
    { dispatch: false }
  );

  notFoundRedirect$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ChecklistActions.LoadModelFail),
        map(() => this.router.navigate(wsLink(SemerisRoutes.checklist.root)))
      ),
    { dispatch: false }
  );

  wsEffect$: Observable<Action> = createWebSocketEffect(
    this.store,
    this.actions$,
    this.webSocketService,
    WebSocketEntities.Checklist,
    () => ChecklistListActions.LoadList()
  );
}
